import { useCallback, useMemo } from 'react';

import { useIsClient } from './useIsClient';
import { FilterFieldDef } from '../components/molecules/FilterBar/AddFilterModal.types';
import {
  filterFieldDefinitionsByVisibleColumns,
  isFieldVisible,
} from '../utils/clientVisibleFieldGuard';

export interface ClientVisibleFieldGuard {
  /** True if the current user is a client/customer (and so restricted). */
  isClient: boolean;
  /**
   * The advanced-filter catalog to feed the FilterBar's `fieldConfig` for
   * client/customer users — restricted to the visible columns the curated
   * report exposes. For non-client users this is `undefined`, signalling the
   * page to pass the full catalog (or no override at all).
   */
  restrictedFieldDefinitions: FilterFieldDef[] | undefined;
  /**
   * Wrap a sort-change handler so it silently no-ops if a client tries to
   * sort by a hidden column. Non-client users get the original handler
   * untouched. Returned identity is stable across renders as long as the
   * inputs are.
   */
  guardSortChange: <M extends { field: string }>(handler: (model: M) => void) => (model: M) => void;
}

/**
 * Lock down filter and sort surfaces to the columns a client/customer user
 * can currently see, per the curated saved-report. Used by /services,
 * /structure, and /parts to keep clients on the rails of "filter and sort
 * by what's been shared with you" — no surfacing hidden fields via the
 * Add Filter modal or a stray sort handler.
 *
 * Non-client users pass through unchanged.
 */
export const useClientVisibleFieldGuard = (
  allFieldDefinitions: ReadonlyArray<FilterFieldDef>,
  visibleFields: ReadonlyArray<string>
): ClientVisibleFieldGuard => {
  const isClient = useIsClient();

  const restrictedFieldDefinitions = useMemo(
    () =>
      isClient
        ? filterFieldDefinitionsByVisibleColumns(allFieldDefinitions, visibleFields)
        : undefined,
    [isClient, allFieldDefinitions, visibleFields]
  );

  const guardSortChange = useCallback(
    <M extends { field: string }>(handler: (model: M) => void) =>
      (model: M) => {
        if (isClient && !isFieldVisible(model.field, visibleFields)) return;
        handler(model);
      },
    [isClient, visibleFields]
  );

  return { isClient, restrictedFieldDefinitions, guardSortChange };
};
